<template>
  <el-dialog
    v-model="visibleModel"
    :title="`登录 — ${platformLabel} / ${accountId}`"
    :width="dialogWidth"
    align-center
    destroy-on-close
    @closed="reset"
  >
    <div v-if="batchTotal > 0" class="batch-progress">
      <div class="batch-progress-label">批量添加进度 {{ batchCurrent }} / {{ batchTotal }}</div>
      <el-progress :percentage="Math.round((batchCurrent / batchTotal) * 100)" :stroke-width="8" />
      <p class="tip">登录成功后才会保留账号；关闭窗口将停止后续创建</p>
    </div>

    <el-alert
      v-if="isXiaohongshu && supplementHint"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom: 12px"
    >
      <template #title>{{ supplementHint.title }}</template>
      <p class="tip">{{ supplementHint.detail }}</p>
    </el-alert>

    <el-alert
      v-else-if="showPrimaryRecommendAlert"
      type="success"
      :closable="false"
      show-icon
      style="margin-bottom: 12px"
    >
      <template #title>{{ primaryRecommendTitle }}</template>
      <p class="tip">{{ primaryRecommendDetail }}</p>
    </el-alert>

    <el-tabs v-model="activeTab">
      <!-- ① 主推：小红书 API -->
      <el-tab-pane v-if="isXiaohongshu" label="API 登录（推荐）" name="xhs-api">
        <XhsApiLoginPanel
          :account-id="accountId"
          :api-base="V2"
          :sessions="xhsSessions"
          @success="onXhsApiSuccess"
        />
      </el-tab-pane>

      <!-- ① 多平台 API 登录（知乎/抖音/头条/百家号） -->
      <el-tab-pane
        v-if="isPlatformApi"
        label="扫码/验证码登录"
        name="platform-api"
      >
        <PlatformApiLoginPanel
          :account-id="accountId"
          :api-base="V2"
          :platform="platform"
          :platform-label="platformLabel"
          :has-cookie="hasCookie"
          @success="onPlatformApiSuccess"
        />
      </el-tab-pane>

      <!-- ① 主推：云端 VNC 独立浏览器（与发布环境一致） -->
      <el-tab-pane
        v-if="interactiveLogin"
        label="独立登录窗口（推荐）"
        name="browser"
      >
        <p class="tip">
          在云端打开<strong>完整浏览器</strong>（可走账号指纹），扫码后 Cookie 自动保存；远程桌面将以<strong>独立弹窗</strong>打开。
        </p>
        <div class="btn-row">
          <el-button
            type="primary"
            :loading="serverBrowserLoading"
            @click="startFreshLogin"
          >
            {{ browserSessionId ? '重新打开云端浏览器' : '打开全新登录窗口' }}
          </el-button>
        </div>
        <p v-if="serverBrowserMessage" class="status-tip">{{ serverBrowserMessage }}</p>
        <el-alert
          v-else-if="!vncWebViewer"
          type="warning"
          :closable="false"
          show-icon
          style="margin-top: 8px"
          title="网页远程桌面未就绪"
        >
          <p class="tip">请重启服务后刷新，或改用下方「备用登录」中的无头截图方式（有风险）。</p>
        </el-alert>
        <VncDesktopPanel
          v-if="vncWebViewer"
          ref="vncPanelRef"
          :visible="interactiveLogin && activeTab === 'browser'"
          :ws-path="vncWsPath"
          :viewer-path="vncViewerPath"
          :auto-large-screen="true"
        />
      </el-tab-pane>

      <!-- ② 滞后：无头截图 + Cookie 上传 + 本机浏览器 -->
      <el-tab-pane label="备用登录（有风险）" name="advanced">
        <el-alert
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 14px"
        >
          <template #title>以下方式仅作备用，存在环境与安全风险</template>
          <ul class="risk-list">
            <li>
              <strong>无头浏览器截图扫码</strong>：后台无界面浏览器截取二维码，部分平台可能识别为异常环境；
              与独立登录窗口相比，指纹/行为一致性较弱，可能增加风控概率。
            </li>
            <li>
              <strong>上传 Cookie JSON</strong>：文件来源不明、过期或与服务器指纹/IP 不一致时，可能导致登录失效或账号异常；
              请勿上传他人 Cookie，注意本地文件泄露。
            </li>
            <li>
              <strong>本机浏览器辅助</strong>：登录态不会自动同步到云端，仍需手动导出并上传 Cookie。
            </li>
          </ul>
        </el-alert>

        <template v-if="supportsServerQrLogin">
          <h4 class="section-title">无头浏览器截图扫码</h4>
          <CloudQrLoginPanel
            ref="cloudQrRef"
            :platform="platform"
            :account-id="accountId"
            :scan-hint="cloudQrScanHint"
            :api-base="V2"
            @success="onCloudQrSuccess"
          />
          <el-divider />
        </template>

        <h4 class="section-title">上传 Cookie JSON</h4>
        <el-upload
          drag
          :auto-upload="false"
          :show-file-list="false"
          accept=".json"
          :on-change="onFile"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">拖拽或点击上传 storage_state / Cookie JSON</div>
        </el-upload>

        <el-divider content-position="left">本机浏览器（辅助）</el-divider>
        <p class="tip warn-tip">
          在本机打开登录页仅方便肉眼扫码，<strong>不会</strong>自动保存到服务器。
        </p>
        <el-button link type="primary" @click="openLoginPopupLegacy">
          在本机浏览器打开
        </el-button>
      </el-tab-pane>
    </el-tabs>

    <template #footer>
      <el-button @click="visibleModel = false">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick, shallowRef } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import axios from 'axios'
import { DEFAULT_VNC_VIEWER_PATH, DEFAULT_VNC_WS_PATH, PUBLISH_V2 } from '../utils/appBase'
import XhsApiLoginPanel from './XhsApiLoginPanel.vue'
import PlatformApiLoginPanel from './PlatformApiLoginPanel.vue'
import CloudQrLoginPanel from './CloudQrLoginPanel.vue'
import VncDesktopPanel from './VncDesktopPanel.vue'

/** 后端支持无头截图扫码的平台（与 SERVER_QR_PLATFORMS 对齐） */
const SERVER_QR_PLATFORMS = new Set([
  'toutiao', 'baijiahao', 'zhihu', 'bilibili', 'xiaohongshu', 'douyin',
])

const CLOUD_QR_SCAN_HINTS = {
  toutiao: '请用「今日头条」App 扫描二维码',
  baijiahao: '请用「百度」App 扫描二维码',
  zhihu: '请用「知乎」App 扫描二维码',
  bilibili: '请用「哔哩哔哩」App 扫描二维码',
  xiaohongshu: '请用「小红书」App 扫描二维码',
  douyin: '请用「抖音」App 扫描二维码',
}

const props = defineProps({
  modelValue: Boolean,
  platform: String,
  accountId: String,
  interactiveLogin: { type: Boolean, default: false },
  vncWebViewer: { type: Boolean, default: false },
  vncViewerPath: { type: String, default: DEFAULT_VNC_VIEWER_PATH },
  vncWsPath: { type: String, default: DEFAULT_VNC_WS_PATH },
  platformLabel: { type: String, default: '' },
  xhsSessions: { type: Object, default: null },
  batchCurrent: { type: Number, default: 0 },
  batchTotal: { type: Number, default: 0 },
})

const emit = defineEmits(['update:modelValue', 'success'])

const V2 = PUBLISH_V2

const visibleModel = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const activeTab = ref('browser')
const isXiaohongshu = computed(() => props.platform === 'xiaohongshu')
const PLATFORM_API_TARGETS = new Set(['zhihu', 'douyin', 'toutiao', 'baijiahao'])
const isPlatformApi = computed(() => PLATFORM_API_TARGETS.has(props.platform))
const supportsServerQrLogin = computed(() => SERVER_QR_PLATFORMS.has(props.platform))
const cloudQrScanHint = computed(
  () => CLOUD_QR_SCAN_HINTS[props.platform] || '请用手机 App 扫描二维码',
)

const showPrimaryRecommendAlert = computed(() => {
  if (isXiaohongshu.value && supplementHint.value) return false
  return isXiaohongshu.value || isPlatformApi.value || props.interactiveLogin
})

const primaryRecommendTitle = computed(() => {
  if (isXiaohongshu.value) return '优先使用 API 登录'
  if (isPlatformApi.value) return '推荐使用扫码/验证码登录'
  if (props.interactiveLogin) return '优先使用独立登录窗口（VNC）'
  return '推荐登录方式'
})

const primaryRecommendDetail = computed(() => {
  if (isXiaohongshu.value) {
    return '通过官方接口扫码/验证码登录，会话与发布链路一致。无头截图与 Cookie 上传请见「备用登录」Tab。'
  }
  if (props.interactiveLogin) {
    return '云端完整浏览器 + 账号指纹，与发布环境一致。无头截图扫码、Cookie JSON 上传已移至「备用登录（有风险）」Tab。'
  }
  return ''
})

const defaultTabForPlatform = (platform) => {
  if (platform === 'xiaohongshu') return 'xhs-api'
  if (PLATFORM_API_TARGETS.has(platform)) return 'platform-api'
  if (props.interactiveLogin) return 'browser'
  return 'advanced'
}

const supplementHint = computed(() => {
  const s = props.xhsSessions
  if (!isXiaohongshu.value || !s) return null
  if (s.pc && !s.creator) {
    return {
      title: '补充登录：Creator 未登录',
      detail: 'PC 已登录，但 API 发布笔记需要 Creator 会话。可一键从 PC 同步，或切换到 Creator 扫码/验证码登录。',
    }
  }
  if (!s.pc && s.creator) {
    return {
      title: '补充登录：PC 未登录',
      detail: 'Creator 已登录，可补充 PC 会话（可选）。已自动切换到 PC 登录。',
    }
  }
  if (s.pc && s.creator) {
    return {
      title: 'PC 与 Creator 均已登录',
      detail: '如需更换登录态，选择对应类型后重新登录即可（仅清除该类型的 Cookie）。',
    }
  }
  return null
})
const serverBrowserLoading = ref(false)
const serverBrowserMessage = ref('')
const dialogWidth = computed(() => '580px')
let browserPollTimer = null
let browserSessionId = ref('')
let autoBrowserStarted = false
const vncPanelRef = shallowRef(null)
const cloudQrRef = shallowRef(null)

const cancelBrowserSession = async () => {
  try {
    await axios.post(`${V2}/cookies/login-browser/cancel-all`)
  } catch {
    /* 忽略 */
  }
}

const cleanupActiveLogin = async () => {
  stopBrowserPoll()
  vncPanelRef.value?.closeLargeScreen?.()
  await cloudQrRef.value?.cancelLogin?.()
  const sid = browserSessionId.value
  browserSessionId.value = ''
  serverBrowserLoading.value = false
  serverBrowserMessage.value = ''
  autoBrowserStarted = false
  await cancelBrowserSession()
}

watch(
  () => props.modelValue,
  (v) => {
    if (v) {
      activeTab.value = defaultTabForPlatform(props.platform)
      autoBrowserStarted = false
      nextTick(() => {
        if (activeTab.value === 'browser' && props.interactiveLogin) {
          void maybeAutoStartBrowserLogin()
        }
      })
    }
  },
)

watch(
  () => [props.platform, props.accountId],
  async (cur, prev) => {
    if (!props.modelValue || !prev) return
    if (cur[0] !== prev[0] || cur[1] !== prev[1]) {
      await cleanupActiveLogin()
    }
  },
)

watch(activeTab, (tab) => {
  if (tab === 'browser' && props.interactiveLogin && props.modelValue) {
    void maybeAutoStartBrowserLogin()
  }
})

const maybeAutoStartBrowserLogin = async () => {
  if (autoBrowserStarted || serverBrowserLoading.value || browserSessionId.value) return
  autoBrowserStarted = true
  try {
    await startServerBrowserLogin()
  } catch (e) {
    autoBrowserStarted = false
    ElMessage.error(e.response?.data?.detail || '无法自动打开登录窗口')
  }
}

const onXhsApiSuccess = async (data) => {
  await finishLoginSuccess({
    ...data,
    platform: 'xiaohongshu',
    account_id: data.canonical_account_id || data.account_id || props.accountId,
  })
}

const onPlatformApiSuccess = async (data) => {
  await finishLoginSuccess(data)
}

const onCloudQrSuccess = async (data) => {
  await finishLoginSuccess(data)
}

const reset = () => {
  void cleanupActiveLogin()
  activeTab.value = defaultTabForPlatform(props.platform)
}

const stopBrowserPoll = () => {
  if (browserPollTimer) {
    clearInterval(browserPollTimer)
    browserPollTimer = null
  }
}

const clearServerSession = async () => {
  await axios.post(`${V2}/cookies/clear-session`, null, {
    params: { platform: props.platform, account_id: props.accountId },
  })
}

const finishLoginSuccess = async (data = {}) => {
  const payload = {
    ...data,
    platform: props.platform,
    account_id: data.canonical_account_id || data.account_id || props.accountId,
  }
  try {
    if (payload.platform && payload.account_id) {
      await axios.post(
        `${V2}/accounts/${payload.platform}/${payload.account_id}/refresh-profile`,
      )
    }
  } catch {
    /* 登录线程可能已同步，忽略 */
  }
  if (data.duplicate_merged || data.redirected) {
    ElMessage.warning(
      data.message
        || (data.duplicate_merged
          ? `该平台账号已存在于 ${data.canonical_account_id || data.account_id}，已用新 Cookie 覆盖原槽位；${data.removed_account_id || props.accountId} 未保留`
          : '登录账号已重定向到已有记录'),
    )
  } else {
    const name = data.display_name || data.profile?.display_name
    ElMessage.success(name ? `登录成功：${name}` : '登录成功，Cookie 已保存')
  }
  stopBrowserPoll()
  serverBrowserLoading.value = false
  emit('success', payload)
  visibleModel.value = false
}

const isBrowserLoginComplete = (data) => {
  if (data?.status !== 'success') return false
  const msg = data.message || ''
  if (msg.includes('正在同步账号资料')) return false
  return Boolean(
    data.display_name
    || data.profile?.display_name
    || msg.includes('Cookie 与资料已保存')
    || msg.includes('已存在于')
    || data.duplicate_merged,
  )
}

const pollBrowserLogin = async () => {
  if (!browserSessionId.value) return
  try {
    const res = await axios.get(`${V2}/cookies/login-browser/${browserSessionId.value}`)
    serverBrowserMessage.value = res.data.message || ''
    if (isBrowserLoginComplete(res.data)) {
      await finishLoginSuccess(res.data)
    } else if (res.data.status === 'success' && (res.data.message || '').includes('资料稍后')) {
      await finishLoginSuccess(res.data)
    } else if (res.data.status === 'cancelled') {
      stopBrowserPoll()
      serverBrowserLoading.value = false
    } else if (res.data.status === 'failed') {
      ElMessage.error(res.data.error || '登录失败')
      stopBrowserPoll()
      serverBrowserLoading.value = false
    }
  } catch (e) {
    console.error(e)
  }
}

const startServerBrowserLogin = async () => {
  if (browserSessionId.value) {
    await cancelBrowserSession()
    browserSessionId.value = ''
  }
  serverBrowserLoading.value = true
  serverBrowserMessage.value = '正在打开全新登录窗口…'
  stopBrowserPoll()
  try {
    await clearServerSession()
    const res = await axios.post(`${V2}/cookies/login-browser`, null, {
      params: {
        platform: props.platform,
        account_id: props.accountId,
        force_relogin: true,
      },
    })
    browserSessionId.value = res.data.session_id
    serverBrowserMessage.value = res.data.message || '请在弹出的浏览器中扫码（未登录状态）'
    serverBrowserLoading.value = false
    browserPollTimer = setInterval(pollBrowserLogin, 1500)
    pollBrowserLogin()
    if (props.vncWebViewer) {
      nextTick(() => vncPanelRef.value?.openLargeScreen?.(true))
    }
  } catch (e) {
    serverBrowserLoading.value = false
    serverBrowserMessage.value = ''
    throw e
  }
}

const startFreshLogin = async () => {
  if (props.interactiveLogin) {
    autoBrowserStarted = false
    browserSessionId.value = ''
    stopBrowserPoll()
    vncPanelRef.value?.resetAutoOpen?.()
    try {
      await startServerBrowserLogin()
      autoBrowserStarted = true
    } catch (e) {
      autoBrowserStarted = false
      ElMessage.error(e.response?.data?.detail || '无法打开独立登录窗口')
    }
    return
  }
  if (props.platform === 'xiaohongshu') {
    activeTab.value = 'xhs-api'
    return
  }
  activeTab.value = 'advanced'
}

const openLoginPopupLegacy = async () => {
  try {
    await clearServerSession()
    const res = await axios.get(`${V2}/cookies/login-urls`, {
      params: { platform: props.platform },
    })
    const { login_url: loginUrl, logout_url: logoutUrl } = res.data
    const w = 520
    const h = 720
    const left = Math.max(0, Math.round((window.screen.width - w) / 2))
    const top = Math.max(0, Math.round((window.screen.height - h) / 2))
    const name = `mp_login_${props.platform}_${Date.now()}`
    const features = `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
    const popup = window.open(logoutUrl || loginUrl, name, features)
    if (!popup) {
      ElMessage.warning('弹窗被拦截，请允许弹窗')
      return
    }
    if (logoutUrl && loginUrl) {
      setTimeout(() => {
        try {
          popup.location.href = loginUrl
        } catch {
          window.open(loginUrl, name, features)
        }
      }, 1500)
    }
    ElMessage.warning(
      '本机浏览器登录不会自动保存到服务器。请导出 storage_state.json 后在「备用登录」中上传。',
    )
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '无法打开登录页')
  }
}

const onFile = async (uploadFile) => {
  const file = uploadFile?.raw
  if (!file || !props.platform || !props.accountId) return
  const fd = new FormData()
  fd.append('file', file)
  try {
    await clearServerSession()
    const res = await axios.post(`${V2}/cookies/upload`, fd, {
      params: { platform: props.platform, account_id: props.accountId },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    if (res.data.has_cookie) {
      try {
        await axios.post(
          `${V2}/accounts/${props.platform}/${res.data.canonical_account_id || props.accountId}/refresh-profile`,
        )
      } catch { /* ignore */ }
      if (res.data.duplicate_merged) {
        ElMessage.warning(
          res.data.message
            || `该平台账号已存在于 ${res.data.canonical_account_id}，已合并到原槽位`,
        )
      } else {
        ElMessage.success('Cookie 已保存到服务器')
      }
      emit('success', {
        ...res.data,
        platform: props.platform,
        account_id: res.data.canonical_account_id || props.accountId,
      })
      visibleModel.value = false
    } else {
      ElMessage.warning('文件已上传，但未检测到有效 Cookie')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '上传失败')
  }
}

onBeforeUnmount(() => {
  void cleanupActiveLogin()
})
</script>

<style scoped>
.batch-progress { margin-bottom: 14px; }
.batch-progress-label { font-size: 14px; font-weight: 500; margin-bottom: 8px; color: #303133; }
.tip { margin: 0; font-size: 13px; line-height: 1.5; color: #606266; }
.warn-tip { font-size: 12px; color: #e6a23c; margin-top: 8px; }
.risk-list {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
}
.risk-list li { margin-bottom: 6px; }
.section-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.btn-row { display: flex; gap: 8px; flex-wrap: wrap; margin: 12px 0; }
.status-tip { font-size: 13px; color: #409eff; margin: 8px 0 0; }
</style>
